import { Access } from "@p0security/shared/types/access/index";

export const GcpServiceAccountAccesses = {
  /** A machine identity standing for one grant in an agentic session.
   *
   * Reached by delegation, never requested directly: an integration that
   * needs an agent workload to hold a Google identity — Google Drive today,
   * another Workspace target tomorrow — delegates here, and grants to the
   * address the identity has. Nothing in this access names the delegating
   * integration, which is what makes it reusable.
   */
  "agentic-identity": Access({
    description: "Machine identity for an agentic session",
    label: "Agentic identity",
    maturity: "beta",
    delegation: {},
    // Nothing waits for approval: every field is fixed when the request is
    // created.
    generated: {},
    parameters: {
      project: {
        type: "input",
        label: "Identity project",
        description: "Google Cloud project the identity is minted in",
      } as const,
      subject: {
        type: "input",
        label: "Session subject",
        description: "Federated subject permitted to impersonate the identity",
      } as const,
      /** The service-account id to mint for this grant, chosen by the
       * delegating integration with `newAgenticIdentityAccountId`.
       *
       * An input, because a delegating integration's values arrive as
       * arguments. Safe as one only because this integration is a bridge:
       * it cannot be requested directly, so the value always comes from P0
       * code rather than from a requestor naming an existing account. */
      accountId: {
        type: "input",
        label: "Service account id",
        description: "Service-account id minted for this grant",
      } as const,
      /** The federated principal permitted to impersonate the identity, as
       * IAM writes it. Resolved at normalize because building it needs the
       * project number and the installed pool, which no synchronous hook can
       * look up. */
      member: {
        type: "generated",
        label: "Impersonation member",
      } as const,
    },
    render: { locator: "project", policy: "subject" } as const,
  }),
};

export type GcpServiceAccountAccesses = typeof GcpServiceAccountAccesses;
export type GcpAgenticIdentityAccess =
  GcpServiceAccountAccesses["agentic-identity"];
